import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import GettingStartedService from '../lib/gettingStartedService';

/**
 * Custom hook for managing Getting Started progress
 * Provides methods to auto-complete steps based on user actions
 */
export const useGettingStarted = () => {
  const { user } = useAuth();
  const { add } = useToast();

  /**
   * Auto-complete step 2 when user creates their first API key
   */
  const completeApiKeyStep = useCallback(async () => {
    if (!user?.id) return;

    try {
      const result = await GettingStartedService.completeApiKeyStep(user.id);
      if (result.success) {
        add({
          title: "Getting Started Progress",
          description: "✅ API Key generation step completed!",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error completing API key step:', error);
    }
  }, [user?.id, add]);

  /**
   * Auto-complete step 3 when user makes their first API call
   */
  const completeFirstApiCallStep = useCallback(async () => {
    if (!user?.id) return;

    try {
      const result = await GettingStartedService.completeFirstApiCallStep(user.id);
      if (result.success) {
        add({
          title: "Getting Started Progress",
          description: "✅ First API call step completed!",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error completing first API call step:', error);
    }
  }, [user?.id, add]);

  /**
   * Auto-complete step 4 when user configures webhooks
   */
  const completeWebhookStep = useCallback(async () => {
    if (!user?.id) return;

    try {
      const result = await GettingStartedService.completeWebhookStep(user.id);
      if (result.success) {
        add({
          title: "Getting Started Progress",
          description: "✅ Webhook configuration step completed!",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error completing webhook step:', error);
    }
  }, [user?.id, add]);

  /**
   * Mark any step as complete
   */
  const completeStep = useCallback(async (stepId) => {
    if (!user?.id) return;

    try {
      const result = await GettingStartedService.markStepComplete(user.id, stepId);
      if (result.success) {
        add({
          title: "Getting Started Progress",
          description: `✅ Step ${stepId} completed!`,
          variant: "default",
        });
      }
      return result;
    } catch (error) {
      console.error(`Error completing step ${stepId}:`, error);
      return { success: false, error: error.message };
    }
  }, [user?.id, add]);

  /**
   * Check if a specific step is completed
   */
  const isStepCompleted = useCallback(async (stepId) => {
    if (!user?.id) return false;

    try {
      const result = await GettingStartedService.isStepCompleted(user.id, stepId);
      return result.completed;
    } catch (error) {
      console.error(`Error checking step ${stepId} completion:`, error);
      return false;
    }
  }, [user?.id]);

  /**
   * Get user's progress summary
   */
  const getProgressSummary = useCallback(async () => {
    if (!user?.id) return null;

    try {
      const result = await GettingStartedService.getProgressSummary(user.id);
      return result.success ? result.data : null;
    } catch (error) {
      console.error('Error fetching progress summary:', error);
      return null;
    }
  }, [user?.id]);

  /**
   * Get the next incomplete step
   */
  const getNextStep = useCallback(async () => {
    if (!user?.id) return null;

    try {
      const result = await GettingStartedService.getNextStep(user.id);
      return result.success ? result.data : null;
    } catch (error) {
      console.error('Error fetching next step:', error);
      return null;
    }
  }, [user?.id]);

  return {
    // Auto-completion methods
    completeApiKeyStep,
    completeFirstApiCallStep,
    completeWebhookStep,
    completeStep,

    // Query methods
    isStepCompleted,
    getProgressSummary,
    getNextStep,

    // Service reference for advanced use
    service: GettingStartedService,
  };
};

export default useGettingStarted;
